import React, { FC } from 'react';
import { Box, Typography } from '@mui/material';
import logoWhite from '../../../assets/logo-white.png';

export interface SplitPanelLayoutProps {
  gradient: string;
  headline: string;
  tagline: string;
  step?: number;
  totalSteps?: number;
  children: React.ReactNode;
}

const SplitPanelLayout: FC<SplitPanelLayoutProps> = ({ gradient, headline, tagline, step, totalSteps, children }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: '#F5F5F5',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, md: 4 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          maxWidth: 960,
          minHeight: 640,
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
        }}
      >
        {/* Left branded panel */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'space-between',
            width: '44%',
            flexShrink: 0,
            background: gradient,
            p: 4,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Decorative circles */}
          <Box
            sx={{
              position: 'absolute',
              top: -80,
              right: -80,
              width: 280,
              height: 280,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.08)',
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -60,
              left: -60,
              width: 220,
              height: 220,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.06)',
              pointerEvents: 'none',
            }}
          />

          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative', zIndex: 1 }}>
            <img src={logoWhite} alt="CoeLab" style={{ height: 28 }} />
          </Box>

          {/* Headline */}
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700, mb: 1.5, lineHeight: 1.25 }}>
              {headline}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.82)', fontSize: 14, lineHeight: 1.6 }}>{tagline}</Typography>
          </Box>

          {/* Step dots */}
          {step !== undefined && totalSteps !== undefined && (
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', gap: 0.75, mb: 0.75 }}>
                {Array.from({ length: totalSteps }).map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: i === step - 1 ? 20 : 8,
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: i === step - 1 ? '#fff' : 'rgba(255,255,255,0.35)',
                      transition: 'width 0.2s ease',
                    }}
                  />
                ))}
              </Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
                Step {step} of {totalSteps}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Right content panel */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: '#fff',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            px: { xs: 3, sm: 5, md: 6 },
            py: 4,
            overflowY: 'auto',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default SplitPanelLayout;
